import axios from 'axios'

export interface Dispute {
  dispute_id: string
  reservation_id: string
  user_id: string
  subject: string
  description: string
  status: 'open' | 'under_review' | 'resolved' | 'rejected' | 'escalated'
  priority: 'low' | 'medium' | 'high'
  resolution?: string
  resolved_by?: string
  resolved_at?: string
  created_at: string
  updated_at: string
  user?: {
    first_name?: string
    last_name?: string
    email?: string
  }
  reservation?: {
    item_name?: string
    merchant_name?: string
    total_amount?: number
    status?: string
  }
  messages?: DisputeMessage[]
}

export interface DisputeMessage {
  message_id: string
  dispute_id: string
  sender_id: string
  sender_name?: string
  is_admin: boolean
  message: string
  created_at: string
}

export interface DisputeStats {
  total: number
  open: number
  under_review: number
  resolved: number
  rejected: number
  escalated: number
  avg_resolution_time_hours: number
  by_priority: { priority: string; count: number }[]
  by_subject: { subject: string; count: number }[]
}

// Dedicated instance for dispute-service.
// Gateway maps /api/disputes → /disputes (prefix preserved), and the gateway
// injects X-User-Id / X-User-Role from the Bearer token in onProxyReq, which is
// how dispute-service derives the authenticated admin (getUserId/getUserRole).
const disputeApi = axios.create({
  baseURL: '/api/disputes',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add request interceptor for authentication
disputeApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Add response interceptor for error handling
disputeApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('access_token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// ---------------------------------------------------------------------------
// Response-shape adapters.
// dispute-service rows use `id`/`reason` and messages use `id`/`sender_role`,
// while the UI interfaces expect `dispute_id`/`subject` and `message_id`/`is_admin`.
// These adapters bridge that gap so callers keep working with the typed shapes.
// ---------------------------------------------------------------------------

const mapDisputeMessage = (msg: any, disputeId: string): DisputeMessage => ({
  message_id: msg.id,
  dispute_id: msg.dispute_id || disputeId,
  sender_id: msg.sender_id,
  sender_name: msg.sender_name,
  is_admin: msg.sender_role === 'admin' || msg.sender_role === 'system',
  message: msg.message,
  created_at: msg.created_at,
})

const mapDisputeRow = (row: any): Dispute => ({
  dispute_id: row.id,
  reservation_id: row.reservation_id,
  user_id: row.user_id,
  subject: row.reason, // backend `reason` column doubles as the subject/category
  description: row.description,
  status: row.status === 'in_review' ? 'under_review' : row.status,
  priority: row.priority,
  resolution: row.resolution,
  resolved_by: row.resolved_by,
  resolved_at: row.resolved_at,
  created_at: row.created_at,
  updated_at: row.updated_at,
  user: row.user,
  reservation: row.reservation,
  messages: Array.isArray(row.messages)
    ? row.messages.map((m: any) => mapDisputeMessage(m, row.id))
    : undefined,
})

export const disputeService = {
  // GET /disputes/admin/all — Admin: list all disputes (paginated)
  // ⚠️ dispute-service returns pagination inside `response.data.pagination.total`
  // (NOT a top-level `total`); we adapt the return to keep { success, data, total }.
  async getDisputes(params?: {
    status?: string
    priority?: string
    search?: string
    page?: number
    limit?: number
  }): Promise<{ success: boolean; data: Dispute[]; total: number }> {
    const response = await disputeApi.get('/admin/all', { params })
    const rows = response.data.data || []
    const total = response.data.pagination?.total ?? rows.length
    return {
      success: response.data.success !== false,
      data: rows.map(mapDisputeRow),
      total,
    }
  },

  // GET /disputes/admin/:id — Admin: dispute details
  // (messages are embedded in the detail response as data.messages)
  async getDispute(disputeId: string): Promise<{ success: boolean; data: Dispute }> {
    const response = await disputeApi.get(`/admin/${disputeId}`)
    return {
      success: response.data.success !== false,
      data: mapDisputeRow(response.data.data),
    }
  },

  // POST /disputes/admin/:id/resolve | POST /disputes/admin/:id/escalate
  // ⚠️ The resolve schema expects { action, reason } with action ∈
  // refund | partial_refund | reject | escalate (reason is required by Joi).
  // The UI passes status 'resolved' | 'rejected', so we map to a valid action:
  //   'rejected' → action 'reject'   (sets status rejected)
  //   'resolved' → action 'refund'   (default action that sets status resolved)
  // The resolve/escalate payload is partial, so we refetch the full dispute
  // for the caller to render complete details.
  async updateDisputeStatus(disputeId: string, status: string, resolution?: string): Promise<{ success: boolean; data: Dispute; message: string }> {
    if (status === 'escalated') {
      const response = await disputeApi.post(`/admin/${disputeId}/escalate`)
      const fresh = await this.getDispute(disputeId)
      return { success: true, data: fresh.data, message: response.data.message || 'Dispute escalated successfully' }
    }

    const action = status === 'rejected' ? 'reject' : 'refund'
    const response = await disputeApi.post(`/admin/${disputeId}/resolve`, {
      action,
      reason: resolution || 'Resolved by admin',
    })

    const fresh = await this.getDispute(disputeId)
    return { success: true, data: fresh.data, message: response.data.message || 'Dispute resolved' }
  },

  // POST /disputes/:id/messages — Add message to dispute (body: { message })
  // Sender is derived server-side from X-User-Id/X-User-Role headers.
  async addMessage(disputeId: string, message: string): Promise<{ success: boolean; data: DisputeMessage }> {
    const response = await disputeApi.post(`/${disputeId}/messages`, { message })
    return {
      success: response.data.success !== false,
      data: mapDisputeMessage(response.data.data, disputeId),
    }
  },

  // There is NO standalone GET /disputes/:id/messages route; messages are
  // embedded in the dispute detail response as data.messages. Reuse getDispute.
  async getMessages(disputeId: string): Promise<{ success: boolean; data: DisputeMessage[] }> {
    const { success, data } = await this.getDispute(disputeId)
    return { success, data: data.messages || [] }
  },

  // GET /disputes/admin/stats — Admin: dispute statistics
  // ⚠️ dispute-service returns { total_disputes, open_count, status_breakdown,
  // reason_breakdown, avg_resolution_time_hours, monthly_trend, ... } — mapped
  // to the DisputeStats interface (no priority breakdown column exists).
  async getStats(): Promise<{ success: boolean; data: DisputeStats }> {
    const response = await disputeApi.get('/admin/stats')
    const s = response.data.data || {}
    const statusCount = (status: string) => {
      const item = (s.status_breakdown || []).find((row: any) => row.status === status)
      return item ? parseInt(item.count, 10) : 0
    }
    return {
      success: response.data.success !== false,
      data: {
        total: parseInt(s.total_disputes, 10) || 0,
        open: parseInt(s.open_count, 10) || 0,
        under_review: statusCount('in_review'),
        resolved: statusCount('resolved'),
        rejected: statusCount('rejected'),
        escalated: statusCount('escalated'),
        avg_resolution_time_hours: s.avg_resolution_time_hours ?? 0,
        by_priority: [], // dispute-service has no priority breakdown column
        by_subject: (s.reason_breakdown || []).map((row: any) => ({
          subject: row.reason,
          count: parseInt(row.count, 10) || 0,
        })),
      },
    }
  },
}

export default disputeService
